import {
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { applyThemeChrome, type ResolvedTheme } from "~/lib/theme-chrome";

export type Theme = "light" | "dark" | "system";
export type { ResolvedTheme } from "~/lib/theme-chrome";

const DEFAULT_STORAGE_KEY = "glider:webapp-v2:theme";
const THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  isLoaded: boolean;
  setTheme: (theme: Theme) => void;
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeState {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isTheme(value: string | null): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

function readStoredTheme(storageKey: string, fallback: Theme): Theme {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const storedTheme = window.localStorage.getItem(storageKey);
    return isTheme(storedTheme) ? storedTheme : fallback;
  } catch {
    return fallback;
  }
}

function persistTheme(storageKey: string, theme: Theme): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, theme);
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

function readSystemMediaQuery(): MediaQueryList | null {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return null;
  }

  try {
    return window.matchMedia(THEME_MEDIA_QUERY);
  } catch {
    return null;
  }
}

function resolveSystemTheme(): ResolvedTheme {
  return readSystemMediaQuery()?.matches ? "dark" : "light";
}

function subscribeToSystemThemeChange(
  onChange: () => void,
): (() => void) | null {
  const mediaQuery = readSystemMediaQuery();
  if (!mediaQuery) {
    return null;
  }

  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", onChange);
    return () => {
      mediaQuery.removeEventListener("change", onChange);
    };
  }

  mediaQuery.addListener(onChange);
  return () => {
    mediaQuery.removeListener(onChange);
  };
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    return resolveSystemTheme();
  }

  return theme;
}

function resolveInitialTheme(defaultTheme: Theme, storageKey: string): Theme {
  return readStoredTheme(storageKey, defaultTheme);
}

function applyThemeClass(resolvedTheme: ResolvedTheme): void {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolvedTheme);
  root.style.colorScheme = resolvedTheme;
  applyThemeChrome(resolvedTheme);
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = DEFAULT_STORAGE_KEY,
}: ThemeProviderProps) {
  const [themeState, setThemeState] = useState<ThemeState>(() => ({
    theme: defaultTheme,
    resolvedTheme: "light",
  }));
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const nextTheme = resolveInitialTheme(defaultTheme, storageKey);
    const nextResolvedTheme = resolveTheme(nextTheme);
    setThemeState({
      theme: nextTheme,
      resolvedTheme: nextResolvedTheme,
    });
    applyThemeClass(nextResolvedTheme);
    setIsLoaded(true);
  }, [defaultTheme, storageKey]);

  useEffect(() => {
    if (!isLoaded || typeof window === "undefined") {
      return;
    }

    const updateResolvedTheme = () => {
      const nextResolvedTheme = resolveTheme(themeState.theme);
      setThemeState((currentThemeState) => {
        if (currentThemeState.theme !== themeState.theme) {
          return currentThemeState;
        }

        if (currentThemeState.resolvedTheme === nextResolvedTheme) {
          return currentThemeState;
        }

        return {
          ...currentThemeState,
          resolvedTheme: nextResolvedTheme,
        };
      });
      applyThemeClass(nextResolvedTheme);
    };

    if (themeState.theme !== "system") {
      return;
    }

    return subscribeToSystemThemeChange(updateResolvedTheme) ?? undefined;
  }, [isLoaded, themeState.theme]);

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      const nextResolvedTheme = resolveTheme(nextTheme);
      setThemeState({
        theme: nextTheme,
        resolvedTheme: nextResolvedTheme,
      });
      applyThemeClass(nextResolvedTheme);
      persistTheme(storageKey, nextTheme);
    },
    [storageKey],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: themeState.theme,
      resolvedTheme: themeState.resolvedTheme,
      isLoaded,
      setTheme,
    }),
    [isLoaded, setTheme, themeState.resolvedTheme, themeState.theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = use(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider.");
  }

  return context;
}
